import type { ReactNode } from 'react';
import { cx } from '../internal/cx';
import { devWarn } from '../internal/dev-warn';

/**
 * Badge de status/modalidade — texto sempre obrigatório (`children`); a cor (`tone`)
 * é reforço visual, nunca o único indicador (mandato: "Status/modalidade nunca só
 * por cor"). `tone="proof"` é a única variante que usa Proof Lime, e só deve ser
 * usada para estado verificado/concluído — o componente avisa em dev se `tone`
 * for `proof` mas o texto não sugerir conclusão/verificação (heurística simples,
 * não bloqueia; a decisão de negócio é do consumidor).
 */
export type StatusBadgeTone = 'neutral' | 'info' | 'proof' | 'danger' | 'warning';

export interface StatusBadgeProps {
  tone?: StatusBadgeTone;
  children: ReactNode;
  /** Ícone opcional — reforço adicional além de texto+cor (ex.: checkmark, relógio). */
  icon?: ReactNode;
  className?: string;
}

const PROOF_HINT_WORDS = [
  'verific',
  'conclu',
  'assinad',
  'aprovad',
  'válid',
  'valid',
  'concluíd',
  'confirmad',
];

export function StatusBadge({ tone = 'neutral', children, icon, className }: StatusBadgeProps) {
  if (tone === 'proof' && typeof children === 'string') {
    const normalized = children.toLowerCase();
    const looksLikeProof = PROOF_HINT_WORDS.some((word) => normalized.includes(word));
    if (!looksLikeProof) {
      devWarn(
        `StatusBadge tone="proof" (Proof Lime) recebeu o texto "${children}", que não parece ` +
          'descrever prova/conclusão/verificação. Lime é reservado a esse estado ' +
          '(BRAND-EXTRACT §4/§7) — confirme se a variante está correta.',
      );
    }
  }

  return (
    <span className={cx('argink-badge', `argink-badge--${tone}`, className)}>
      {icon ? (
        <span className="argink-badge__icon" aria-hidden="true">
          {icon}
        </span>
      ) : null}
      <span className="argink-badge__label">{children}</span>
    </span>
  );
}
